/*
 * Cupom de desconto na barra lateral do carrinho.
 *
 * O carrinho AJAX da Shopify aceita o campo `discount` no /cart/update:
 *   { discount: "CODIGO" }  aplica (e substitui o anterior)
 *   { discount: "" }        remove
 * e a resposta ja vem com o carrinho atualizado, incluindo
 *   discount_codes: [{ code, applicable }]
 * que diz se a loja aceitou o codigo. Esse campo e a fonte de verdade da
 * validacao: cobre cupom de valor, de produto e de FRETE GRATIS (que nao muda
 * o total do carrinho e por isso nao daria para detectar olhando o preco).
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.cart-drawer__discount-input');
    this.applyButton = this.querySelector('.cart-drawer__discount-apply');
    this.messageElement = this.querySelector('.cart-drawer__discount-message');

    if (this.applyButton) {
      this.applyButton.addEventListener('click', () => this.apply());
    }

    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.apply();
      });
      this.input.addEventListener('input', () => this.clearMessage());
    }

    this.querySelectorAll('.cart-drawer__discount-remove').forEach((button) => {
      button.addEventListener('click', () => this.remove(button.dataset.discountCode));
    });

    this.restorePendingState();
  }

  get appliedCodes() {
    return (this.dataset.appliedCodes || '').split(',').filter(Boolean);
  }

  get strings() {
    return window.cartDiscountStrings || {};
  }

  get cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  get cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update';
  }

  /*
   * O elemento e substituido a cada re-render, entao a mensagem e o que o
   * cliente digitou viajam por uma propriedade estatica e sao reaplicados no
   * connectedCallback do elemento novo.
   */
  restorePendingState() {
    const state = CartDiscount.pendingState;
    CartDiscount.pendingState = null;
    if (!state) return;

    if (state.value && this.input) this.input.value = state.value;
    if (state.message) this.setMessage(state.message, state.status);
    if (state.focus && this.input) this.input.focus();
  }

  async apply() {
    if (!this.input) return;

    const code = this.input.value.trim();
    if (!code) {
      this.setMessage(this.strings.empty, 'error');
      this.input.focus();
      return;
    }

    const previousCodes = this.appliedCodes;
    this.setLoading(true);

    try {
      const cart = await this.setSessionDiscount(code);

      if (this.isApplicable(cart, code)) {
        CartDiscount.pendingState = { status: 'success', message: this.strings.success };
      } else {
        // A sessao ja guardou o codigo recusado: devolvemos o cupom anterior
        // para o cliente nao perder um desconto que estava valendo.
        await this.setSessionDiscount(previousCodes.join(','));
        CartDiscount.pendingState = {
          status: 'error',
          message: (this.strings.invalid || '').replace('[code]', code),
          value: code,
          focus: true,
        };
      }

      this.render(await this.fetchSections());
    } catch (error) {
      console.error(error);
      this.setLoading(false);
      this.setMessage(this.strings.error, 'error');
    }
  }

  async remove(code) {
    const remaining = this.appliedCodes.filter((applied) => applied !== code);
    this.setLoading(true, code);

    try {
      // Confere no carrinho que voltou em vez de assumir que deu certo: foi
      // exatamente isso que fez a versao anterior dizer "removido" com o cupom
      // ainda aplicado.
      const cart = await this.setSessionDiscount(remaining.join(','));
      const aindaPresente = (cart.discount_codes || []).some(
        (discount) => discount.code.toLowerCase() === code.toLowerCase()
      );

      CartDiscount.pendingState = aindaPresente
        ? { status: 'error', message: this.strings.error }
        : { status: 'success', message: this.strings.removed };

      this.render(await this.fetchSections());
    } catch (error) {
      console.error(error);
      this.setLoading(false);
      this.setMessage(this.strings.error, 'error');
    }
  }

  /* Codigo vazio remove o cupom da sessao. Devolve o carrinho ja atualizado. */
  async setSessionDiscount(code) {
    const response = await fetch(this.cartUpdateUrl, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: code }),
    });

    if (!response.ok) throw new Error(`cart update failed with ${response.status}`);

    return response.json();
  }

  isApplicable(cart, code) {
    return (cart.discount_codes || []).some(
      (discount) => discount.applicable && discount.code.toLowerCase() === code.toLowerCase()
    );
  }

  /*
   * O drawer entra por {% render %} e o icone do carrinho e inline no header,
   * entao nenhum dos dois e instancia de secao na pagina e ?sections= nao os
   * encontraria. ?section_id= renderiza o arquivo da secao isoladamente — e o
   * mesmo caminho que CartItems.onCartUpdate ja usa neste tema.
   */
  async fetchSections() {
    const [drawer, icon] = await Promise.all([
      this.fetchSection('cart-drawer'),
      this.fetchSection('cart-icon-bubble'),
    ]);

    return { drawer, icon };
  }

  async fetchSection(sectionId) {
    const response = await fetch(`${this.cartUrl}?section_id=${sectionId}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`section ${sectionId} failed with ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  render(sections) {
    if (!sections.drawer) return;

    // Mesma troca cirurgica que CartItems.onCartUpdate faz: preserva a nota do
    // pedido, o overlay e os listeners do proprio cart-drawer. O cart-discount
    // vem por ultimo porque a troca desconecta este elemento.
    ['cart-drawer-items', '.cart-drawer__footer', 'cart-discount'].forEach((selector) => {
      const target = document.querySelector(selector);
      const source = sections.drawer.querySelector(selector);
      if (target && source) target.replaceWith(source);
    });

    this.updateCartIcon(sections.icon);
    document.dispatchEvent(new CustomEvent('cart:discount-updated'));
  }

  /*
   * O listener que abre o drawer vive em #cart-icon-bubble, entao trocamos so os
   * textos do icone em vez do elemento inteiro.
   */
  updateCartIcon(section) {
    if (!section) return;

    ['.wbhcartitem', '.wbcarthtotal'].forEach((selector) => {
      const target = document.querySelector(`#cart-icon-bubble ${selector}`);
      const source = section.querySelector(`#cart-icon-bubble ${selector}`);
      if (target && source) target.innerHTML = source.innerHTML;
    });
  }

  setLoading(isLoading, code) {
    this.classList.toggle('cart-drawer__discount--loading', isLoading);

    const button = code
      ? this.querySelector(`.cart-drawer__discount-remove[data-discount-code="${CSS.escape(code)}"]`)
      : this.applyButton;

    if (button) {
      button.classList.toggle('loading', isLoading);
      button.disabled = isLoading;
    }

    if (this.input) this.input.disabled = isLoading;
  }

  setMessage(text, status) {
    if (!this.messageElement || !text) return;

    this.messageElement.textContent = text;
    this.messageElement.classList.toggle('cart-drawer__discount-message--error', status === 'error');
    this.messageElement.classList.toggle('cart-drawer__discount-message--success', status === 'success');
    this.messageElement.hidden = false;
  }

  clearMessage() {
    if (!this.messageElement) return;

    this.messageElement.hidden = true;
    this.messageElement.textContent = '';
  }
}

CartDiscount.pendingState = null;

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
